import React from 'react';
import AuthorisationBatchVM from './AuthorisationBatchVM';
import { observer } from 'mobx-react';
import { Neo, NeoGrid } from '@singularsystems/neo-react';
import LetterAuthorisationLookup from '../../../Models/LetterAuthorisation/Lookups/LetterAuthorisationLookup';
import { ReportViewComponent } from '@singularsystems/neo-reporting';
import * as Roles from '../../../Models/Security/Roles';
import ExportLetterCommand from '../../../Models/LetterAuthorisation/Commands/ExportLetterCommand';
import { ReportType } from '../../../Models/LetterAuthorisation/ReportType';
import LetterAuthorisationBatchLookup from "../../../Models/LetterAuthorisation/Lookups/LetterAuthorisationBatchLookup";
import { ModalUtils } from "@singularsystems/neo-core";

interface IAuthorisationBatchProps {
    viewModel: AuthorisationBatchVM;
    onBackClicked: () => void;
}

@observer
export default class AuthorisationBatch extends React.Component<IAuthorisationBatchProps> {

    constructor(props: IAuthorisationBatchProps) {
        super(props);
    }

    public render() {
        const viewModel = this.props.viewModel,
            batch = viewModel.batch;

        if (!batch) {
            return undefined;
        }

        return (
            <div>
                <Neo.Card title="Letter Authorisation" icon="stamp">
                    <div className="mb-4 d-flex justify-content-between">
                        <Neo.Button variant="light" icon="arrow-left" onClick={() => this.props.onBackClicked()}>Back</Neo.Button>

                        <div>
                            {!batch.isAuthorised && (viewModel.authorisation.hasRole(Roles.LetterAuthorisation.AuthoriseWithoutSignatures) || batch.remaining === 0) &&
                                <Neo.Button 
                                    pulse={batch.remaining === 0} 
                                    icon="flag-checkered"
                                    tooltip={batch.remaining === 0 ? "" : "Mark the batch as complete. Unauthorised letters will use the default signatories."}
                                    onClick={() => viewModel.authoriseBatch()}>Complete</Neo.Button>}
                            {batch.isAuthorised &&
                                <>
                                    <Neo.Button
                                        icon="undo"
                                        variant="light"
                                        tooltip="Re-open the batch for further editing"
                                        onClick={() => viewModel.unauthoriseBatch()}>Re-open</Neo.Button>
                                    {batch.allowEmail && 
                                        <Neo.Button
                                            icon="paper-plane"
                                            className="ms-2"
                                            onClick={() => viewModel.beginExport()}>Send all...</Neo.Button>}
                                    <Neo.Button
                                        icon="cloud-upload-alt"
                                        className="ms-2"
                                        onClick={() => viewModel.saveAsDocument()}>Save all</Neo.Button>
                                </>}
                        </div>
                    </div>
                
                    <Neo.GridLayout lg={4}>
                        <Neo.FormGroup display={batch.meta.reportTypeName} />
                        {batch.hasDifferentDates && 
                            <Neo.FormGroup label="Start Date" display={batch.meta.reportStartDate} />}
                        <Neo.FormGroup display={batch.meta.reportDate} />
                        <Neo.FormGroup display={batch.meta.entity} />
                        {batch.description && 
                            <Neo.FormGroup display={batch.meta.description} label="Description" />}
                        <Neo.FormGroup display={batch.meta.signatoryNames} label="Signatories" />
                    </Neo.GridLayout>

                    {batch.unlinkedRecordCount > 0 &&
                        <Neo.Alert variant="warning" className="mt-2">
                            Note: There are {batch.unlinkedRecordCount} record(s) on this date not linked to a letter batch.

                            {!batch.isAuthorised && <div className="mt-2">
                                <Neo.Button onClick={() => viewModel.addUnlinkedRecordsToBatch()}>
                                    Add records to this batch
                                </Neo.Button>
                            </div>}
                        </Neo.Alert>}
                </Neo.Card>

                
                <Neo.Card>
                    <Neo.GridLayout>
                        <Neo.FormGroupInline bind={viewModel.letterCriteria.meta.incomplete} label="Incomplete only?" />
                        <Neo.FormGroupInline bind={viewModel.letterCriteria.meta.requireSigningBy} 
                            select={{ items: batch.requiredSignatories, valueMember: "signatoryId" }} 
                            label="Pending Signing by" />
                    </Neo.GridLayout>

                    <Neo.Pager pageManager={viewModel.pageManager}>
                        <NeoGrid.Grid<LetterAuthorisationLookup>>
                            {(item, meta) => (
                                <NeoGrid.Row>
                                    <NeoGrid.Column display={meta.refNo} width={150} />
                                    <NeoGrid.Column display={meta.description} width={300} />
                                    {viewModel.letterCriteria.reportType !== ReportType.InterCompanyDividendLetter &&
                                        <NeoGrid.Column display={meta.amount} width={200} />}
                                    <NeoGrid.Column display={meta.signedBy}  />
                                    <NeoGrid.Column display={meta.recordIsAuthorised} label="Record Authorised?" />
                                    <NeoGrid.Column display={meta.sentOn} />
                                    <NeoGrid.ButtonColumn>
                                        <Neo.Button variant="info" icon="stamp" size="sm" onClick={() => viewModel.viewReport(item)}>View</Neo.Button>
                                    </NeoGrid.ButtonColumn>
                                    {(batch.allowEmail && (batch.isAuthorised || viewModel.pageManager.data.some(c => c.isComplete))) &&
                                        <NeoGrid.ButtonColumn>
                                            {(batch.isAuthorised || item.isComplete) && 
                                                <Neo.Button
                                                    icon="paper-plane"
                                                    size="sm"
                                                    variant="secondary"
                                                    isOutline
                                                    onClick={() => viewModel.beginExport(item)}>Send</Neo.Button>}
                                            
                                        </NeoGrid.ButtonColumn>}
                                    {(batch.isAuthorised || viewModel.pageManager.data.some(c => c.isComplete)) &&
                                        <NeoGrid.ButtonColumn>
                                            {(batch.isAuthorised || item.isComplete) && 
                                                <>
                                                    {!item.fileId &&
                                                        <Neo.Button
                                                            icon="cloud-upload-alt"
                                                            size="sm"
                                                            variant="secondary"
                                                            isOutline
                                                            tooltip="Save the letter as a permanent document"
                                                            onClick={() => viewModel.saveAsDocument(item)}>Save</Neo.Button>}
                                                    {item.fileId && 
                                                        <Neo.Button
                                                            icon="download"
                                                            size="sm"
                                                            variant="secondary"
                                                            isOutline
                                                            onClick={() => viewModel.downloadDocument(item)}>Download</Neo.Button>}
                                                </>}                                            
                                        </NeoGrid.ButtonColumn>}
                                    {viewModel.authorisation.hasRole(Roles.LetterAuthorisation.Delete) &&
                                        <NeoGrid.ButtonColumn deleteButton={{ onClick: () => this.beginDeleteLetter(item)}} />}
                                </NeoGrid.Row>
                            )}
                        </NeoGrid.Grid>
                    </Neo.Pager>
                </Neo.Card>

                {viewModel.reportViewVM && 
                    <Neo.Modal 
                        title={viewModel.reportViewVM.reportInfo.displayName} 
                        size="xl" 
                        className="report-modal" 
                        show={viewModel.reportViewVM.showModal} 
                        onClose={() => viewModel.reportViewVM!.hide()}>

                        <ReportViewComponent viewModel={viewModel.reportViewVM} />

                        {this.renderAuthorisationComponents(batch)}
                    </Neo.Modal>}

                <Neo.Modal 
                    title="Send"
                    bindModel={viewModel.meta.exportCommand}
                    acceptButton={{ text: "Export", icon: "paper-plane", onClick: () => viewModel.doExport()}}>
                    {(command: ExportLetterCommand) => (
                        <div>
                            <Neo.FormGroup display={command.meta.emailAddress} hidden={command.letterAuthorisationId == null} />
                            <Neo.FormGroup bind={command.meta.overrideEmailAddress} placeholder="No override email address" />
                            <Neo.FormGroup display={command.meta.isLetterPasswordProtected} label={"Password protect?"} hidden={command.letterAuthorisationId == null} />
                            <Neo.FormGroup display={command.meta.letterPassword} hidden={command.letterAuthorisationId == null} />
                            <Neo.FormGroup bind={command.meta.overridePassword} placeholder="No override password" hidden={command.letterAuthorisationId == null} />
                            {command.hasSent &&
                                <div>
                                    <Neo.Alert variant="warning">
                                        {command.letterAuthorisationId === null && <span>Some letters have already been exported. Tick to confirm re-export.</span>}
                                        {command.letterAuthorisationId !== null && <span>Letter has already been exported. Tick to confirm re-export.</span>}
                                    </Neo.Alert>
                                    <Neo.FormGroup bind={command.meta.reExport} />
                                </div>}
                        </div>
                    )}
                </Neo.Modal>
            </div>
        );
    }

    private renderAuthorisationComponents(batch: LetterAuthorisationBatchLookup) {
        const viewModel = this.props.viewModel;

        if (!viewModel.letterInfo) {
            return undefined;
        }

        const recordIsAuthorised = !viewModel.requiresRecordToBeAuthorised || viewModel.letterInfo.letter.recordIsAuthorised;
 
        return viewModel.letterInfo && !batch.isAuthorised &&
            <div className="mt-3 text-center">
                {!viewModel.letterInfo.signatoriesAllowed.some(c => c.allowedToSign) &&
                    <Neo.Alert variant="warning">
                        You are not authorised to sign this letter.
                    </Neo.Alert>}
                {!recordIsAuthorised &&
                    <Neo.Alert variant="warning">
                        Cannot authorise letter, linked record is not authorised.
                    </Neo.Alert>}
                {viewModel.letterInfo.signatoriesAllowed.filter(c => c.allowedToSign).map(sig => (
                    <Neo.Button disabled={!recordIsAuthorised && !sig.hasSigned} variant={sig.hasSigned ? "danger" : "success"} icon="stamp" className="me-2" onClick={() => viewModel.signReport(sig)}>
                        {sig.hasSigned ? "Reject" : "Sign"} for {sig.display}
                    </Neo.Button>
                ))}
            </div>
    }

    private beginDeleteLetter(letter: LetterAuthorisationLookup) {

        ModalUtils.showYesNoDismissible("Delete letter", "Are you sure you want to delete this letter?", 
            () => this.props.viewModel.deleteLetter(letter));
    }
}